import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { resolveCountryFlagIconUrl } from '../../../utils/country-flag.util';

export interface CompanyCountrySelectOption {
  value: string;
  label: string;
  iso?: string;
  iconUrl?: string;
}

/**
 * Países activos de una empresa.
 * GET {apiUrl}/selects/company-countries?companyId=
 */
@Injectable({ providedIn: 'root' })
export class CompanyCountriesSelectService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly cache = new Map<string, CompanyCountrySelectOption[]>();
  private readonly endpointUrl = '/selects/company-countries';

  getOptions(companyId: string): Observable<CompanyCountrySelectOption[]> {
    const id = companyId.trim();
    if (!id) return of([]);

    const cached = this.cache.get(id);
    if (cached) return of(cached);

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}${this.endpointUrl}`, {
        params: { companyId: id },
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => this.cache.set(id, options)),
        catchError(() => of([])),
      );
  }

  invalidate(companyId?: string): void {
    if (!companyId?.trim()) {
      this.cache.clear();
      return;
    }
    this.cache.delete(companyId.trim());
  }

  private parseSelectResponse(body: unknown): CompanyCountrySelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{
          id: string;
          code?: string;
          text?: string;
          label?: string;
          name?: string;
          iconUrl?: string;
        }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => {
        const iso = item.code?.trim().toUpperCase() || undefined;
        return {
          value: item.id || '',
          label: item.text || item.label || item.name || '',
          iso,
          iconUrl: resolveCountryFlagIconUrl({
            iso,
            iconUrl: item.iconUrl,
          }),
        };
      });
    } catch {
      return [];
    }
  }
}
